/**
 * RoleSwitcher — compact top-bar menu that lets a user with multiple roles
 * (e.g. gym owner + trainer) hop between their dashboards without going back
 * to Settings. Renders nothing if the user only has the base `member` role.
 *
 * Uses `GET /api/me/roles` to decide which options to surface.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutGrid, Check, Dumbbell, Apple, Building2, Store, Sparkles, ShieldCheck, Crown } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";

const VIEW_META = {
  member:       { label: "Member view",       path: "/home",                  Icon: Sparkles,    fg: "#0FB5A6" },
  trainer:      { label: "Trainer Portal",    path: "/pro/portal",            Icon: Dumbbell,    fg: "#00B0D5" },
  nutritionist: { label: "Nutritionist Portal", path: "/pro/portal",          Icon: Apple,       fg: "#7A67D5" },
  gym_owner:    { label: "Arena Dashboard",   path: "/arena/dashboard",       Icon: Building2,   fg: "#B58A00" },
  vendor:       { label: "Marketplace Seller",path: "/marketplace/sell",      Icon: Store,       fg: "#D85825" },
  admin:        { label: "Admin",             path: "/admin/professionals",   Icon: ShieldCheck, fg: "#1A1A1A" },
  superadmin:   { label: "Super Admin",       path: "/superadmin",            Icon: Crown,       fg: "#E5484D" },
};

export default function RoleSwitcher({ variant = "light" }) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [roles, setRoles] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    apiClient.get("/me/roles").then((r) => setRoles(r.data)).catch(() => setRoles(null));
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!roles) return null;

  // Any pending application (pro/gym/vendor) — surface a yellow dot on the pill.
  const anyPending = !!(roles.pending?.pro || roles.pending?.gym || roles.pending?.vendor);

  // Compile the list — member is always available for any signed-in user.
  const available = ["member"];
  if (roles.roles.trainer) available.push("trainer");
  if (roles.roles.nutritionist) available.push("nutritionist");
  if (roles.roles.gym_owner) available.push("gym_owner");
  if (roles.roles.vendor) available.push("vendor");
  if (roles.roles.admin) available.push("admin");
  if (roles.roles.superadmin) available.push("superadmin");

  // Don't render for pure members — no other view to switch to.
  if (available.length <= 1) return null;

  const currentKey =
    available.find((k) => VIEW_META[k].path === location.pathname) ||
    available.find((k) => location.pathname.startsWith(VIEW_META[k].path)) ||
    "member";
  const CurrentIcon = VIEW_META[currentKey].Icon;

  const isDark = variant === "dark";
  const chipBg = isDark ? "rgba(255,255,255,0.10)" : "#FFFFFF";
  const chipBorder = isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E7E5E1";
  const chipFg = isDark ? "#F4F3F1" : "#1A1A1A";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Switch view"
        data-testid="role-switcher-btn"
        aria-expanded={open}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px 6px 8px",
          borderRadius: 100,
          background: chipBg,
          border: chipBorder,
          color: chipFg,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <CurrentIcon size={13} color={VIEW_META[currentKey].fg} strokeWidth={2.4} />
        <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {VIEW_META[currentKey].label}
        </span>
        <LayoutGrid size={12} opacity={0.6} />
        {anyPending && (
          <span
            aria-label="Application under review"
            data-testid="role-switcher-pending-dot"
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#F5C518",
              border: `2px solid ${isDark ? "#0B0B0B" : "#F4F3F1"}`,
              boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
            }}
          />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            data-testid="role-switcher-menu"
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              minWidth: 220,
              background: "#FFFFFF",
              border: "1px solid #E7E5E1",
              borderRadius: 14,
              boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
              padding: 6,
              zIndex: 60,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#8A8884",
                fontWeight: 700,
                padding: "8px 10px 4px",
              }}
            >
              Switch view
            </div>
            {available.map((key) => {
              const m = VIEW_META[key];
              const Icon = m.Icon;
              const active = key === currentKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (!active) navigate(m.path);
                  }}
                  data-testid={`role-switch-${key}`}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 10px",
                    borderRadius: 10,
                    background: active ? "#F4F3F1" : "transparent",
                    border: 0,
                    cursor: active ? "default" : "pointer",
                    textAlign: "left",
                    color: "#1A1A1A",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 8,
                      background: `${m.fg}22`,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={14} color={m.fg} strokeWidth={2.4} />
                  </span>
                  <span style={{ flex: 1 }}>{m.label}</span>
                  {active && <Check size={14} color="#0FB5A6" strokeWidth={3} />}
                </button>
              );
            })}

            {anyPending && (
              <div data-testid="role-switcher-pending-list" style={{ marginTop: 4, borderTop: "1px solid #E7E5E1", paddingTop: 6 }}>
                <div
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 10,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#B58A00",
                    fontWeight: 700,
                    padding: "6px 10px 4px",
                  }}
                >
                  Under review
                </div>
                {roles.pending?.pro && (
                  <PendingRow icon={Dumbbell} fg="#00B0D5" label="Professional application" testid="pending-pro" />
                )}
                {roles.pending?.gym && (
                  <PendingRow icon={Building2} fg="#B58A00" label="Gym listing" testid="pending-gym" />
                )}
                {roles.pending?.vendor && (
                  <PendingRow icon={Store} fg="#D85825" label="Vendor application" testid="pending-vendor" />
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PendingRow({ icon: Icon, fg, label, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 10px",
        borderRadius: 10,
        background: "transparent",
        color: "#1A1A1A",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: 8,
          background: `${fg}22`,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={14} color={fg} strokeWidth={2.4} />
      </span>
      <span style={{ flex: 1, color: "#4A4845" }}>{label}</span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: "3px 8px",
          borderRadius: 100,
          background: "rgba(245,197,24,0.16)",
          color: "#B58A00",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Pending
      </span>
    </div>
  );
}
